import type { ReactElement } from 'react';
import { displayMaterialLineKindFromApi } from '../../domain/procurementRequest';
import { FilterTiles } from '../master/FilterTiles';
import { GroupedDataGrid, type GroupedColumnDef } from '../grid/GroupedDataGrid';
import { GridPaginationBar } from './GridPaginationBar';

export type InventoryGridRow = {
  id: string;
  inventoryNumber: string;
  materialLineKind: string;
  sku: string;
  region: string;
  trackFootage: boolean;
  quantityOnHand: number;
  footageOnHand?: number | null;
  currentDeliveryAddress: string;
  afeNumber: string;
};

const INVENTORY_COLUMNS: GroupedColumnDef[] = [
  { id: 'inventoryNumber', label: 'Number', header: 'Number' },
  { id: 'materialLineKind', label: 'Kind', header: 'Kind' },
  { id: 'sku', label: 'SKU', header: 'SKU' },
  { id: 'region', label: 'Region', header: 'Region' },
  { id: 'onHand', label: 'On hand', header: 'On hand' },
  { id: 'location', label: 'Location / AFE', header: 'Location / AFE' }
];

function getGroupValue(row: InventoryGridRow, columnId: string): string {
  switch (columnId) {
    case 'inventoryNumber':
      return row.inventoryNumber ?? '—';
    case 'materialLineKind':
      return displayMaterialLineKindFromApi(row.materialLineKind);
    case 'sku':
      return row.sku ?? '—';
    case 'region':
      return row.region ?? '—';
    case 'onHand':
      return row.trackFootage ? `${row.footageOnHand ?? 0} ft` : `${row.quantityOnHand} qty`;
    case 'location':
      return `${row.afeNumber || '—'} ${row.currentDeliveryAddress || ''}`.trim();
    default:
      return '—';
  }
}

export interface InventoryGridProps {
  error?: string | null;
  rows: InventoryGridRow[];
  loading: boolean;
  selectedId: string | null;
  onSelectRow: (id: string) => void;
  page: number;
  pageSize: number;
  totalCount: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  materialLineKindFilter: string;
  onMaterialLineKindFilterChange: (value: string) => void;
  skuFilter: string;
  onSkuFilterChange: (value: string) => void;
}

export function InventoryGrid({
  error,
  rows,
  loading,
  selectedId,
  onSelectRow,
  page: currentPage,
  pageSize,
  totalCount,
  onPageChange,
  onPageSizeChange,
  materialLineKindFilter,
  onMaterialLineKindFilterChange,
  skuFilter,
  onSkuFilterChange
}: InventoryGridProps): ReactElement {
  function renderRow(row: InventoryGridRow): ReactElement {
    return (
      <tr
        className={selectedId === row.id ? 'selected' : undefined}
        onClick={() => onSelectRow(row.id)}
        onDoubleClick={(e) => {
          e.preventDefault();
          onSelectRow(row.id);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onSelectRow(row.id);
          }
        }}
        role="button"
        tabIndex={0}
      >
        <td className="mono-id">{row.inventoryNumber}</td>
        <td>{displayMaterialLineKindFromApi(row.materialLineKind)}</td>
        <td className="mono-id">{row.sku}</td>
        <td>{row.region}</td>
        <td>
          {row.trackFootage ? `${row.footageOnHand ?? 0} ft` : `${row.quantityOnHand} qty`}
        </td>
        <td>
          <div>{row.afeNumber || '—'}</div>
          <div className="hint" style={{ fontSize: '0.85em' }}>
            {row.currentDeliveryAddress || '—'}
          </div>
        </td>
      </tr>
    );
  }

  const kindCounts = new Map<string, number>();
  for (const row of rows) {
    const key = (row.materialLineKind ?? '').trim() || 'unknown';
    kindCounts.set(key, (kindCounts.get(key) ?? 0) + 1);
  }
  const tileValue = materialLineKindFilter.trim() || 'all';

  return (
    <>
    <FilterTiles
      testId="inventory-tiles"
      value={tileValue}
      onChange={(id) => onMaterialLineKindFilterChange(id === 'all' ? '' : id)}
      options={[
        { id: 'all', label: 'All', count: totalCount },
        ...[...kindCounts.entries()].map(([id, count]) => ({
          id,
          label: displayMaterialLineKindFromApi(id),
          count
        }))
      ]}
    />
    <section className="card requests-grid-card">
      <div className="row spread requests-grid-toolbar">
        <h2>Inventory</h2>
      </div>
      <p className="hint">
        Lots are created when a purchase order is marked <strong>paid</strong> (or added manually by procurement). Open{' '}
        <strong>Material Transfer</strong> in the nav to move or scrap stock. Click or double-click a row for the
        details panel. Use the filters and column controls like other grids (sort, filter, group by dragging column
        titles).
      </p>
      {error && <p className="danger-text">{error}</p>}
      <div className="row filter-row" style={{ flexWrap: 'wrap', alignItems: 'end', gap: '0.75rem' }}>
        <label htmlFor="inv-filter-kind" style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
          <span>Material line kind</span>
          <input
            id="inv-filter-kind"
            type="search"
            className="grid-col-filter"
            style={{ minWidth: '12rem' }}
            placeholder="Filter API results…"
            value={materialLineKindFilter}
            onChange={(e) => onMaterialLineKindFilterChange(e.target.value)}
            autoComplete="off"
            aria-label="Filter list by material line kind"
          />
        </label>
        <label htmlFor="inv-filter-sku" style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
          <span>SKU</span>
          <input
            id="inv-filter-sku"
            type="search"
            className="grid-col-filter"
            style={{ minWidth: '10rem' }}
            placeholder="Filter API results…"
            value={skuFilter}
            onChange={(e) => onSkuFilterChange(e.target.value)}
            autoComplete="off"
            aria-label="Filter list by SKU"
          />
        </label>
      </div>
      {loading && <p className="hint">Loading inventory…</p>}
      <GroupedDataGrid<InventoryGridRow>
        columns={INVENTORY_COLUMNS}
        rows={rows}
        getRowId={(r) => r.id}
        getGroupValue={getGroupValue}
        renderRow={renderRow}
        colSpan={6}
        emptyMessage="No inventory rows in your regions (or no matches for the API filters above)."
        groupHint="Sort and filter apply to the current page. Use Material line kind / SKU to narrow the server list. Drag
          the grip (⠿) beside a title into the strip to group."
      />
      <GridPaginationBar
        idPrefix="inventory-grid"
        page={currentPage}
        pageSize={pageSize}
        totalCount={totalCount}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
      />
    </section>
    </>
  );
}
